import type {Asset,Placement,PlacementInput,Surface} from './types';
import {ALPHA_THRESHOLD,CELL_SIZE,gridSize} from './paint';
import {surfaceContains} from './surfaceRegions';

/** Historical contributions stay intact; visibility is derived from erase events. */
export function eraseEventsFor(paint:Placement,events:readonly Placement[]):Placement[] {
  return paint.operation==='erase'?[]:events.filter(event=>event.operation==='erase'&&event.surfaceId===paint.surfaceId&&paint.sequence<=(event.eraseCutoff??-1));
}

/** Canonical no-op test uses the same normalized physical footprint as charging.
 * Previously erased hidden layers never become eligible again. */
export function eraseHasImpact(surface:Surface,input:PlacementInput,eraseCells:readonly number[],events:readonly Placement[],assets:ReadonlyMap<string,Asset>):boolean {
  const eraseAsset=assets.get(input.assetId);if(!eraseAsset)return false;
  const columns=gridSize(surface).columns;
  return events.some(paint=>{
    if(paint.removed||paint.operation==='erase'||paint.surfaceId!==surface.id||paint.sequence>(input.eraseCutoff??-1))return false;
    const asset=assets.get(paint.assetId);if(!asset)return false;
    const earlier=eraseEventsFor(paint,events);
    return eraseCells.some(cell=>{
      const left=cell%columns*CELL_SIZE,top=Math.floor(cell/columns)*CELL_SIZE;
      for(let dy=.5;dy<CELL_SIZE;dy++)for(let dx=.5;dx<CELL_SIZE;dx++){
        const x=left+dx,y=top+dy;if(!surfaceContains(surface,x,y)||alphaAt(eraseAsset,input,x,y)<ALPHA_THRESHOLD/255)continue;
        let alpha=alphaAt(asset,paint,x,y);
        for(const event of earlier){const mask=assets.get(event.assetId);if(mask)alpha*=1-alphaAt(mask,event,x,y);}
        if(alpha>=ALPHA_THRESHOLD/255)return true;
      }return false;
    });
  });
}

function alphaAt(asset:Asset,pose:Pick<Placement,'x'|'y'|'width'|'height'|'rotation'|'opacity'>,x:number,y:number):number {
  const radians=-(pose.rotation??0)*Math.PI/180,dx=x-pose.x-pose.width/2,dy=y-pose.y-pose.height/2;
  const u=(dx*Math.cos(radians)-dy*Math.sin(radians))/pose.width+.5,v=(dx*Math.sin(radians)+dy*Math.cos(radians))/pose.height+.5;
  if(u<0||u>=1||v<0||v>=1)return 0;
  return asset.mask.alpha[Math.floor(v*asset.mask.height)*asset.mask.width+Math.floor(u*asset.mask.width)]/255*(pose.opacity??1);
}
